import React, { useState, useEffect } from 'react';
import './Etape7.css';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';
import pendingPhotosService from '../services/pendingPhotosService';
import ObservationModal from './modals/ObservationModal';
import QuickPhotoButton from './QuickPhotoButton';

function Etape7({ mission, onBack, onSave }) {
  const [formData, setFormData] = useState({
    sans_objet: false,
    installations: []
  });

  const [currentInstallation, setCurrentInstallation] = useState({
    emplacement: '',
    seuil_assujettissement: '',
    effectif_public: '',
    effectif_personnel: '',
    effectif_total: '',
    type_evacuation: false,
    type_anti_panique: false,
    energie_alimentee: '',
    type_canalisation: '',
    telecommande_repos: false,
    numero_obs: '',
    commentaires: ''
  });

  const [editingIndex, setEditingIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [observationContext, setObservationContext] = useState(null);
  const [obsInitialPhotos, setObsInitialPhotos] = useState([]);
  const [obsPendingPhotoId, setObsPendingPhotoId] = useState(null);

  // 🔥 Fonction pour convertir une valeur en booléen de manière fiable
  const toBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (value === 'true' || value === 'oui' || value === 1 || value === '1') return true;
    return false;
  };

  useEffect(() => {
    loadSavedData();
  }, []);

  const loadSavedData = async () => {
    try {
      const saved = await inspectionStorage.getStep(mission.id, 'etape_8_securite');
      if (saved) {
        // 🔥 Convertir les booléens dans les installations
        const fixedInstallations = (saved.installations || []).map(inst => ({
          ...inst,
          type_evacuation: toBoolean(inst.type_evacuation),
          type_anti_panique: toBoolean(inst.type_anti_panique),
          telecommande_repos: toBoolean(inst.telecommande_repos)
        }));
        
        setFormData({
          ...saved,
          sans_objet: toBoolean(saved.sans_objet),
          installations: fixedInstallations
        });
        console.log('📦 Données Étape 7 récupérées depuis le cache');
      }
    } catch (error) {
      console.error('❌ Erreur chargement cache:', error);
    }
  };

  const saveStepData = async (data) => {
    const stepData = {
      ...data,
      timestamp: new Date().toISOString()
    };
    
    // 1. Sauvegarder en local (IndexedDB)
    await inspectionStorage.saveStep(mission.id, 'etape_8_securite', stepData);
    
    // 2. 🔥 AUTO-SYNC vers Base44 (si en ligne)
    syncManager.syncStep(mission.id, 'etape_8_securite', stepData);
  };

  const updateCurrentInstallation = (field, value) => {
    setCurrentInstallation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addInstallation = async () => {
    if (currentInstallation.emplacement) {
      // 🔥 S'assurer que les booléens sont bien des booléens avant de sauvegarder
      const installationToSave = {
        ...currentInstallation,
        id: editingIndex !== null ? formData.installations[editingIndex]?.id : Date.now(),
        type_evacuation: toBoolean(currentInstallation.type_evacuation),
        type_anti_panique: toBoolean(currentInstallation.type_anti_panique),
        telecommande_repos: toBoolean(currentInstallation.telecommande_repos)
      };
      
      let updatedInstallations;
      if (editingIndex !== null) {
        updatedInstallations = [...formData.installations];
        updatedInstallations[editingIndex] = installationToSave;
      } else {
        updatedInstallations = [...formData.installations, installationToSave];
      }
      
      const newFormData = {
        ...formData,
        installations: updatedInstallations
      };
      
      setFormData(newFormData);
      await saveStepData(newFormData);
      
      resetCurrentInstallation();
      setEditingIndex(null);
    }
  };

  const resetCurrentInstallation = () => {
    setCurrentInstallation({
      emplacement: '',
      seuil_assujettissement: '',
      effectif_public: '',
      effectif_personnel: '',
      effectif_total: '',
      type_evacuation: false,
      type_anti_panique: false,
      energie_alimentee: '',
      type_canalisation: '',
      telecommande_repos: false,
      numero_obs: '',
      commentaires: ''
    });
  };

  const editInstallation = (index) => {
    const installation = formData.installations[index];
    // 🔥 Convertir les booléens lors de l'édition
    setCurrentInstallation({
      ...installation,
      type_evacuation: toBoolean(installation.type_evacuation),
      type_anti_panique: toBoolean(installation.type_anti_panique),
      telecommande_repos: toBoolean(installation.telecommande_repos)
    });
    setEditingIndex(index);
  };

  const cancelEdit = () => {
    resetCurrentInstallation();
    setEditingIndex(null);
  };

  const removeInstallation = async (id) => {
    const newFormData = {
      ...formData,
      installations: formData.installations.filter(inst => inst.id !== id)
    };
    setFormData(newFormData);
    await saveStepData(newFormData);
  };

  const updateField = async (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };
    setFormData(newFormData);
    await saveStepData(newFormData);
  };

  const buildInstallationContext = (installation, index) => {
    const typeEclairage = [
      toBoolean(installation.type_evacuation) && 'Évacuation',
      toBoolean(installation.type_anti_panique) && 'Anti-panique',
    ].filter(Boolean).join(' + ') || 'Non défini';

    return {
      step_origin: 'etape_7_securite',
      localisation: installation.emplacement,
      element: `Éclairage de sécurité - ${typeEclairage}`,
      circuit: '',
      element_ref: {
        type: 'installation_securite',
        installationId: installation.id,
        index: index,
        seuil: installation.seuil_assujettissement,
        energie: installation.energie_alimentee,
      },
    };
  };

  const openObsModalWith = (ctx, initialPhotos = [], pendingPhotoId = null) => {
    setObservationContext(ctx);
    setObsInitialPhotos(initialPhotos);
    setObsPendingPhotoId(pendingPhotoId);
    setShowObservationModal(true);
  };

  const openObservationForInstallation = (installation, index) => {
    openObsModalWith(buildInstallationContext(installation, index));
  };

  const handleQuickPhotoOpenObs = (photo, ctx) => {
    if (!photo) return;
    const url = photo.url || photo.blob_base64;
    openObsModalWith(ctx, url ? [url] : [], photo.id);
  };

  const handleCloseObsModal = () => {
    setShowObservationModal(false);
    setObservationContext(null);
    setObsInitialPhotos([]);
    setObsPendingPhotoId(null);
  };

  const handleSaveObservation = async (observationData) => {
    try {
      const etape11Data = await inspectionStorage.getStep(mission.id, 'etape_11_observations') || {};
      const currentObservations = etape11Data.observations || [];
      
      const dernierNumero = currentObservations.length > 0
        ? Math.max(...currentObservations.map(o => o.numero || 0))
        : 0;
      const nouveauNumero = dernierNumero + 1;
      
      const observation = {
        ...observationData,
        numero: nouveauNumero,
        created_date: observationData.created_date || new Date().toISOString(),
        photos: observationData.photos || []
      };
      
      const updatedObservations = [...currentObservations, observation];
      
      const obsStepData = {
        observations: updatedObservations,
        dernier_numero: nouveauNumero,
        timestamp: new Date().toISOString()
      };
      
      await inspectionStorage.saveStep(mission.id, 'etape_11_observations', obsStepData);
      
      // 🔥 AUTO-SYNC observations vers Base44
      syncManager.syncStep(mission.id, 'etape_11_observations', obsStepData);

      if (observationContext?.element_ref?.type === 'installation_securite') {
        const installationId = observationContext.element_ref.installationId;
        const updatedInstallations = formData.installations.map(inst => 
          inst.id === installationId 
            ? { ...inst, numero_obs: nouveauNumero.toString() }
            : inst
        );
        
        const newFormData = { ...formData, installations: updatedInstallations };
        setFormData(newFormData);
        await saveStepData(newFormData);
      }

      console.log('✅ Observation sauvegardée:', nouveauNumero);

      if (obsPendingPhotoId) {
        try {
          await pendingPhotosService.remove(mission.id, obsPendingPhotoId);
        } catch (e) {
          console.warn('[Etape7] suppression pending photo échouée :', e?.message || e);
        }
      }

      alert(`✅ Observation N°${nouveauNumero} créée avec succès !`);
    } catch (error) {
      console.error('❌ Erreur sauvegarde observation:', error);
      alert('Erreur lors de la sauvegarde de l\'observation');
    }

    handleCloseObsModal();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveStepData(formData);
      
      console.log('✅ Étape 7 (sécurité) sauvegardée localement');
      
      if (onSave) {
        onSave(formData);
      }

      alert('✅ Étape 7 sauvegardée avec succès !');
      
      onBack();
      
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="etape-container">
      <div className="etape-header">
        <button className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <div className="etape-title">
          <h1>Étape 7/14</h1>
          <p>Installations de sécurité</p>
        </div>
        <div></div>
      </div>

      <div className="etape-content">
        
        <div className="form-section sans-objet-section">
          <label className="checkbox-card sans-objet-card">
            <input
              type="checkbox"
              checked={formData.sans_objet === true}
              onChange={(e) => updateField('sans_objet', e.target.checked)}
            />
            <span>🚫 Sans Objet - Installation non soumise à l'éclairage de sécurité</span>
          </label>
          <p className="helper-text">
            Cochez cette case si l'établissement n'est pas soumis aux dispositions relatives à l'éclairage de sécurité
          </p>
        </div>

        {!formData.sans_objet && (
          <>
            {formData.installations.length > 0 && (
              <div className="success-message">
                ✅ Installation de sécurité ajoutée avec succès
              </div>
            )}

            <div className="form-section securite-form">
              <h2>🛡️ Éclairage de sécurité</h2>
              <h3>{editingIndex !== null ? 'Modifier l\'installation' : 'Ajouter une installation d\'éclairage de sécurité'}</h3>
              
              <div className="info-grid">
                <div className="form-group">
                  <label className="required">Emplacement / Local</label>
                  <input
                    type="text"
                    value={currentInstallation.emplacement}
                    onChange={(e) => updateCurrentInstallation('emplacement', e.target.value)}
                    className="form-input"
                    placeholder="Ex: BOUTIQUE, Couloir principal..."
                  />
                </div>

                <div className="form-group">
                  <label>Seuil assujettissement</label>
                  <select
                    value={currentInstallation.seuil_assujettissement}
                    onChange={(e) => updateCurrentInstallation('seuil_assujettissement', e.target.value)}
                    className="form-select"
                  >
                    <option value="">-- Sélectionner --</option>
                    <option value="ERP">ERP</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="750">750</option>
                    <option value="1000">1000</option>
                    <option value="1500">1500</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Effectif Public</label>
                  <input
                    type="number"
                    value={currentInstallation.effectif_public}
                    onChange={(e) => updateCurrentInstallation('effectif_public', e.target.value)}
                    className="form-input"
                    placeholder="Ex: 10"
                  />
                </div>

                <div className="form-group">
                  <label>Effectif Personnel</label>
                  <input
                    type="number"
                    value={currentInstallation.effectif_personnel || ''}
                    onChange={(e) => updateCurrentInstallation('effectif_personnel', e.target.value)}
                    className="form-input"
                    placeholder="Ex: 2"
                  />
                </div>

                <div className="form-group">
                  <label>Effectif Total</label>
                  <input
                    type="number"
                    value={currentInstallation.effectif_total}
                    onChange={(e) => updateCurrentInstallation('effectif_total', e.target.value)}
                    className="form-input"
                    placeholder="Ex: 12"
                  />
                </div>
              </div>

              <div className="info-grid">
                <div className="form-group">
                  <label>Type : Évacuation</label>
                  <div className="radio-inline">
                    <label className="radio-option-inline">
                      <input
                        type="radio"
                        name="type_evacuation"
                        checked={currentInstallation.type_evacuation === true}
                        onChange={() => updateCurrentInstallation('type_evacuation', true)}
                      />
                      <span>Oui</span>
                    </label>
                    <label className="radio-option-inline">
                      <input
                        type="radio"
                        name="type_evacuation"
                        checked={currentInstallation.type_evacuation === false}
                        onChange={() => updateCurrentInstallation('type_evacuation', false)}
                      />
                      <span>Non</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Type : Anti-panique</label>
                  <div className="radio-inline">
                    <label className="radio-option-inline">
                      <input
                        type="radio"
                        name="type_anti_panique"
                        checked={currentInstallation.type_anti_panique === true}
                        onChange={() => updateCurrentInstallation('type_anti_panique', true)}
                      />
                      <span>Oui</span>
                    </label>
                    <label className="radio-option-inline">
                      <input
                        type="radio"
                        name="type_anti_panique"
                        checked={currentInstallation.type_anti_panique === false}
                        onChange={() => updateCurrentInstallation('type_anti_panique', false)}
                      />
                      <span>Non</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="info-grid">
                <div className="form-group">
                  <label>Énergie alimenté par</label>
                  <select
                    value={currentInstallation.energie_alimentee}
                    onChange={(e) => updateCurrentInstallation('energie_alimentee', e.target.value)}
                    className="form-select"
                  >
                    <option value="">-- Sélectionner --</option>
                    <option value="Source centralisée">Source centralisée</option>
                    <option value="Bloc autonome">Bloc autonome</option>
                    <option value="Groupe électrogène">Groupe électrogène</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Type canalisation</label>
                  <select
                    value={currentInstallation.type_canalisation}
                    onChange={(e) => updateCurrentInstallation('type_canalisation', e.target.value)}
                    className="form-select"
                  >
                    <option value="">-- Sélectionner --</option>
                    <option value="CR1">CR1 (Résistant au feu)</option>
                    <option value="C2">C2</option>
                    <option value="C1">C1</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Présence de télécommande de mise au repos</label>
                <div className="radio-inline">
                  <label className="radio-option-inline">
                    <input
                      type="radio"
                      name="telecommande_repos"
                      checked={currentInstallation.telecommande_repos === true}
                      onChange={() => updateCurrentInstallation('telecommande_repos', true)}
                    />
                    <span>Oui</span>
                  </label>
                  <label className="radio-option-inline">
                    <input
                      type="radio"
                      name="telecommande_repos"
                      checked={currentInstallation.telecommande_repos === false}
                      onChange={() => updateCurrentInstallation('telecommande_repos', false)}
                    />
                    <span>Non</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Commentaires</label>
                <textarea
                  value={currentInstallation.commentaires}
                  onChange={(e) => updateCurrentInstallation('commentaires', e.target.value)}
                  className="form-textarea"
                  rows={3}
                  placeholder="Observations, état de fonctionnement, problèmes constatés..."
                />
              </div>

              <div className="form-actions-inline">
                <button
                  onClick={addInstallation}
                  className="btn btn-add-installation"
                  disabled={!currentInstallation.emplacement}
                >
                  ➕ {editingIndex !== null ? 'METTRE À JOUR' : 'AJOUTER'}
                </button>
                {editingIndex !== null && (
                  <button
                    onClick={cancelEdit}
                    className="btn btn-secondary"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>

            {formData.installations.length > 0 && (
              <div className="installations-section">
                <h3>🛡️ Installations d'éclairage de sécurité ({formData.installations.length})</h3>
                
                <div className="installations-grid">
                  {formData.installations.map((installation, index) => (
                    <div key={installation.id} className="installation-card">
                      <div className="installation-header">
                        <div className="installation-title">{installation.emplacement}</div>
                        <div className="installation-actions">
                          <button
                            onClick={() => openObservationForInstallation(installation, index)}
                            className="btn-icon btn-obs"
                            title="Ajouter une observation"
                          >
                            ⚠️
                          </button>
                          <QuickPhotoButton
                            missionId={mission?.id}
                            context={buildInstallationContext(installation, index)}
                            onOpenObservation={handleQuickPhotoOpenObs}
                            title="Prendre une photo de l'installation"
                          />
                          <button
                            onClick={() => editInstallation(index)}
                            className="btn-icon btn-edit"
                            title="Modifier"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => removeInstallation(installation.id)}
                            className="btn-icon btn-delete"
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="installation-body">
                        {installation.seuil_assujettissement && (
                          <p><strong>Seuil:</strong> {installation.seuil_assujettissement}</p>
                        )}
                        <p>
                          <strong>Type:</strong>{' '}
                          {toBoolean(installation.type_evacuation) ? 'Évacuation' : ''}
                          {toBoolean(installation.type_evacuation) && toBoolean(installation.type_anti_panique) ? ' + ' : ''}
                          {toBoolean(installation.type_anti_panique) ? 'Anti-panique' : ''}
                          {!toBoolean(installation.type_evacuation) && !toBoolean(installation.type_anti_panique) ? 'Non défini' : ''}
                        </p>
                        {installation.energie_alimentee && (
                          <p><strong>Énergie:</strong> {installation.energie_alimentee}</p>
                        )}
                        {installation.type_canalisation && (
                          <p><strong>Canalisation:</strong> {installation.type_canalisation}</p>
                        )}
                        {installation.numero_obs && (
                          <p className="obs-badge"><strong>OBS:</strong> <span className="badge-obs">{installation.numero_obs}</span></p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="etape-actions">
        <button 
          className="btn btn-secondary" 
          onClick={onBack}
          disabled={isSaving}
        >
          Annuler
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? '💾 Sauvegarde...' : '💾 Sauvegarder et continuer'}
        </button>
      </div>

      {showObservationModal && (
        <ObservationModal
          isOpen={showObservationModal}
          onClose={handleCloseObsModal}
          onSave={handleSaveObservation}
          context={observationContext}
          initialPhotos={obsInitialPhotos}
          missionId={mission?.id}
        />
      )}
    </div>
  );
}

export default Etape7;